// i18n exemption (deliberate, panel-wide): the workflow chat/report surfaces
// have no i18n coverage yet — every string here is plain English by the same
// convention as TroubleshootingPanels.tsx. Migrating the family is tracked in
// the workflow backlog (chat i18n); don't piecemeal-translate single strings.
import { useState } from 'react'
import ReactMarkdown, { type Options as ReactMarkdownOptions } from 'react-markdown'
import { CircleCheck, AlertTriangle, CircleX, ChevronDown, ChevronRight, FileText, Activity, Database, Info, ShieldAlert } from 'lucide-react'

import { cn } from '@/lib/utils'
import { remarkPlugins, rehypePlugins, mergeMarkdownComponents } from '@/config/markdownConfig'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { describeEvidenceSource } from './evidenceSources'
import { isSerializedStructureBlob, parseSokRefusal } from './reportUtils'
import {
  openQuestionCount,
  resultHasAnswer,
  GENERAL_LEAD_KEYS,
  formatGeneralScalar,
  generalInline,
  generalResultKeys,
  humanizeGeneralKey,
  isScaffoldingKey,
  sectionItems,
} from './generalResultGuard'
import type { WorkflowRunView, WorkflowStepRunView } from './workflowTypes'
import { isV2Run, workflowStatusLabel } from './workflowTypes'

// Nested `[plugin, options]` tuples in markdownConfig are not assignable to
// react-markdown's PluggableList. Same cast the chat surface already uses.
const remarkPluginList = remarkPlugins as ReactMarkdownOptions['remarkPlugins']
const rehypePluginList = rehypePlugins as ReactMarkdownOptions['rehypePlugins']

interface WorkflowResultPanelProps {
  run: WorkflowRunView | null
  steps?: WorkflowStepRunView[]
  onSelectStep?: (step: WorkflowStepRunView) => void
  /** Opens this workflow's Report tab. Omitted where the panel IS the report. */
  onViewReport?: () => void
}

/**
 * Renders the troubleshooting artifact (`run.finalOutput`) as a scannable result
 * for a plant engineer: the verdict, then the reasoning (problem → cause →
 * actions), then a collapsed citation list. It deliberately does NOT dump the
 * internal investigation transcript — per-tool `sourceRefs`, `workflow:…:tool:N`
 * ids, and OCR chunk previews are plumbing, not findings, so they are summarized
 * or hidden rather than recursed into.
 */
/**
 * The answer, RENDERED — not printed.
 *
 * Feedback doc item 10, "lack of formatting in responses": the answer arrived as markdown and was
 * dropped into a `<p>`, so a reader got `**Propane Dehydrators (Q69-C-161A/B):**` with the
 * asterisks showing and every bullet run into one paragraph. The engine had done the work of
 * structuring it and the panel un-did it.
 *
 * Uses the SAME markdown config the chat surfaces use (`markdownConfig`) rather than a private
 * one, so a table, a list or a code span looks the same wherever an answer appears — and so a
 * later hardening of that config (sanitisation, link handling) covers this panel too.
 */
function AnswerProse({ text, className }: { text: string; className?: string }) {
  return (
    <div
      className={cn(
        'prose prose-sm max-w-none text-foreground dark:prose-invert',
        // The panel is a card, not an article: kill the outer margins the typography plugin adds
        // so the first line still sits tight under the status pill.
        '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={remarkPluginList}
        rehypePlugins={rehypePluginList}
        components={mergeMarkdownComponents()}
      >
        {text}
      </ReactMarkdown>
    </div>
  )
}

export function WorkflowResultPanel({
  run,
  steps = [],
  onSelectStep,
  onViewReport,
}: WorkflowResultPanelProps) {
  if (!run?.finalOutput) return null

  const output = run.finalOutput as Record<string, unknown>
  const refusal = parseSokRefusal(output)
  const provisional = provisionalSource(output)
  const moves = periodMoves(output)
  const method = methodFrom(output)
  const generated = generatedFrom(output)
  const sources = usedSources(output)
  const omitted = measuredOmitted(output)
  const documents = documentsOmitted(output)
  const basis = basisOmitted(output)
  const prior = priorRuns(output)
  const review = reviewVerdict(output)
  const outcome = asRecord(output.outcome)
  const evidenceStepById = buildEvidenceStepMap(steps)
  // The banner grades what this artifact CONTAINS (shared decider — same one the
  // report pill and the Markdown export use), not the engine's honesty status: a
  // fully populated result under a warning triangle is the reported defect.
  const delivered = deliveredVerdict(run.status, output)
  const tone = outcomeTone(run.status, delivered)
  // A CLEAN delivery reads as an ANSWER, not as an open investigation: the
  // troubleshooting scaffolding (the problem's expected/actual/extent rows, the
  // safety line, the investigation actions) is noise once the question is
  // answered, and it makes two answered questions of different shapes look
  // unrelated. A run with open questions or a failure keeps every section —
  // that is exactly when the reader needs the diagnostic detail.
  // Only collapse when something a reader would call the ANSWER survives the collapse.
  // Without this guard a run whose only finding is its recommended actions rendered the
  // problem statement and NOTHING else under a green verdict — an empty-looking report
  // claiming a clean run (caught by probing the populated-section matrix).
  // Each disjunct is a section that RENDERS IN FULL under concision. `outcome` findings
  // are deliberately NOT one: the banner's verdict line names only the FIRST
  // (`Root cause identified: …`), so a run whose findings live only there would lose the
  // rest to the collapse. Such a run keeps every section instead — the collapse is a
  // tidiness win and is never worth dropping a finding for.
  const answerSurvivesConcision =
    Boolean(str(asRecord(output.primaryCandidate)?.statement)) ||
    sectionItems('analysisNarratives', output.analysisNarratives).length > 0 ||
    generalResultKeys(output).length > 0
  const concise = delivered?.clean === true && answerSurvivesConcision
  const ToneIcon = tone.icon
  const verdict = verdictLine(outcome, output, run.planPreview?.goal, run.planPreview?.family)
  const panelTitle = resultPanelTitle(run, output)

  // blockedClaims / assumptions live under `outcome` in the schema, but the normalizer sometimes lifts
  // them top-level — read both. `remainingUncertainty` is deliberately NOT read here: nothing renders it
  // any more (see `OpenQuestionsSection`), and threading an unread prop through is what failed the lint
  // and build gates on the first attempt.
  const blockedClaims = firstArray(outcome?.blockedClaims, output.blockedClaims)
  const assumptions = firstArray(outcome?.assumptions, output.assumptions)

  // Identity strip for the banner: what was investigated + with which method.
  const problem = asRecord(output.problemStatement)
  const subjects = asArray(problem?.subjects).map(asRecord).filter(Boolean) as Record<string, unknown>[]
  const operators = asArray(output.operatorsApplied).map(str).filter(Boolean)
  const hasMeta = subjects.length > 0 || operators.length > 0

  return (
    <section className="border-t border-border bg-background">
      <div className="flex flex-col gap-5 p-4">
        {/* Verdict banner — color-coded by status, with a one-line plain reading. */}
        <div className={cn('rounded-xl border p-4', tone.box)}>
          <div className="flex items-center gap-2">
            <ToneIcon className={cn('size-4 shrink-0', tone.iconAccent)} />
            <h2 className="text-sm font-semibold text-foreground">{panelTitle}</h2>
            {/* Which ENGINE produced this run. Added because there was no way to tell:
                v1 and v2 render from the same tables, so a v1 run read exactly like a v2
                one — dev run e08723f0 was reported as a v2 test when it was v1 with the
                flag on. Shown ONLY for v2, so a v1 run looks exactly as it does today. */}
            {isV2Run(run) && (
              <span
                className="rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
                title="Ran on the workflow-v2 engine (source-of-knowledge gated)"
              >
                v2
              </span>
            )}
            {/* The REVIEW AGENT's verdict. It checks that the answer addresses what the
                question named and that every identifier it asserts appears in a gathered row —
                deterministically, so it cannot confirm a wrong answer the way a model asked
                "is this good?" would. Reported, never applied: the answer still ships. */}
            {/* SHOWN ONLY WHEN IT CHANGES WHAT THE READER SHOULD TRUST.

                Jenna, feedback doc item 13: "what is review? If I as a user have no control for
                setting certain fields then I do not need to know what value they are set to." A
                passing internal check is not news — it was `review: holds` on a good answer,
                which is our vocabulary spent on telling a plant engineer that nothing happened.
                A FAILING one is different: it says part of the answer is unsupported, which is
                worth the space and reads in v1's own words ("open questions") rather than in
                ours ("review: 2 issues"). */}
            {review && !review.holds && (
              <span
                className={cn(
                  'rounded-full border px-2 py-0.5 text-[11px] font-medium',
                  'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300',
                )}
                title={review.issues.join(' · ')}
              >
                {`${review.issues.length} open ${review.issues.length === 1 ? 'question' : 'questions'}`}
              </span>
            )}
            <span className={cn('ml-auto rounded-full px-2 py-0.5 text-[11px] font-medium', tone.pill)}>
              {delivered?.label ?? workflowStatusLabel(run.status)}
            </span>
          </div>
          {verdict && <AnswerProse text={verdict} className="mt-1.5 text-sm" />}
          {/* A v2 SOK refusal lands as `blocked` + `finalOutput.text` with no `outcome`,
              so `verdictLine` is null and the reader otherwise only sees a red pill
              (soft #28609). Show the stored refusal when there is no structured verdict. */}
          {!verdict && str(output.text) !== '' && (
            <AnswerProse text={str(output.text)} className="mt-1.5 text-sm" />
          )}
          {/* The ACTIONABLE half of a refusal, which until now was stored and never shown.
              The refusal prose deliberately names no method — a message that enumerated
              what IS covered would emit "root cause" in every refusal and the acceptance
              check could not tell a real decline from a soft match that refused in words
              (`blueprint-matcher.ts`). So the specifics travel as STRUCTURED data for this
              panel to render, and without rendering them the reader sees only the prose and
              a red pill: a decline that reads as a flat wall rather than as a named gap
              with two ways to close it. */}
          {/* The answer rests on ONE named person's UNREVIEWED account of the method — a
              weaker claim than a signed-off procedure, and an answer that cannot say which
              it is invites the reader to assume the stronger. The blueprint behind it is a
              draft and is never published, so nobody else's question is answered from it. */}
          {/* WHICH unreviewed thing it is. Every suite-1 run on 2026-09-03 was footed "Method
              supplied by <the reader> … Saved as a draft procedure pending sign-off" — nothing was
              supplied and no draft was written; those runs started from the v1 canvas, where the
              label means "this plan is one person's approved guess". QC round 1 filed it as a
              phantom draft with wrong provenance.

              THREE KINDS, TWO SENTENCES, and that is deliberate. `supplied` is the only one that
              may say a draft is pending sign-off, and it is stamped only where one exists.
              `canvas` and `approved` differ in WHICH path produced them — a canvas Run versus an
              approved-plan resume — which is a telemetry distinction, not a reader-facing one:
              both mean "one person's approved plan, nothing in the library", so both get that
              sentence. Two near-identical strings differing only in "not" vs "not yet" would be a
              distinction a reader cannot act on and a copy-review trap.

              AND A FOURTH STATE THAT ASSERTS NOTHING, for a record with no `kind` at all. Every
              producer stamps one today (`provisionalSourceKind` names the four sites), so an
              unstamped record is a run stored BEFORE the field existed — and both of the sentences
              above would make a claim about what that person did. A legacy run that really was a
              supplied draft footed "Plan approved by …", which is weaker than the old default's lie
              but still a false statement (review on #30896, and greptile P1 on the same lines). So
              the unknown case shows the attribution and says only what is certain from the record's
              own existence: unreviewed, and nothing in the library. */}
          {provisional && (
            <p className="mt-2 rounded-md bg-background/70 px-2 py-1.5 text-[11px] text-muted-foreground ring-1 ring-border">
              {provisional.kind === 'unstamped' ? (
                <>
                  Plan attributed to{' '}
                  <span className="font-medium text-foreground">{provisional.attributedTo}</span>, not
                  reviewed as a procedure. Nothing was saved to the library.
                </>
              ) : provisional.kind === 'canvas' || provisional.kind === 'approved' ? (
                <>
                  Plan approved by{' '}
                  <span className="font-medium text-foreground">{provisional.attributedTo}</span> and not
                  reviewed as a procedure. Nothing was saved to the library.
                </>
              ) : (
                <>
                  Method supplied by{' '}
                  <span className="font-medium text-foreground">{provisional.attributedTo}</span> and not
                  yet reviewed. Saved as a draft procedure pending sign-off.
                </>
              )}
            </p>
          )}
          {/* THE PLAN CAME FROM THE PLANT'S OWN PROCEDURE, and the reader cannot otherwise tell.
              A run that found a method on file and one that authored a plan from the question read
              identically in this panel, which is the distinction the whole method route exists to
              make — and the first line of what a correct answer to this class of question owes:
              which documents were found, and that they became the plan's ground truth. */}
          {/* AN OFFER BESIDE THE ANSWER, NOT A GATE BEFORE IT.
              A method found on file is the best the catalog had, and it is not always a PROCEDURE.
              Dev 2026-09-04 run `d1583f86`: "Did the depropanizer overhead pressure ever go above
              its alarm limit in the first half of October 2022? And confirm we stayed inside
              design." planned from `Q69 OIM# 9520.004` —

                [WORKFLOW_V2_DOC_PLAN]     outcome=compiled plan=document steps=7 degraded=no
                [WORKFLOW_V2_METHOD_ROUTE] ran=found-method doc=Q69 OIM# 9520.004 …

              — and answered from one instrument, with no row count, no margin, no basis check and
              no dataset caveat. That document really does carry the alarm table (320 psig and
              345 psig are correct and came out of it); what it does not carry is a way to VERIFY
              against it. A document that gives you a number is not one that tells you how to check.

              THE ENGINE CANNOT TELL WHICH IT HAS, and the two attempts to make it tell are why this
              is a line of text rather than a gate: a pre-planning judge asked to separate "the
              corpus holds a procedure" from "the corpus holds a manual" answered `not_a_method`
              seven times out of seven and took `ran=found-method` from 26 runs a day to zero,
              because it PARKED instead of answering. Offering costs a reader one sentence; gating
              costs them the answer.

              UNCONDITIONAL on a corpus-sourced method, deliberately. It claims nothing about
              whether this document is the right one — it states where the method came from, which
              this block already did, and that a written one overrides it. No classifier decides
              when to show it, so there is nothing to be wrong about.

              NAMES NO CONTROL, AND PROMISES NOTHING ABOUT THIS RUN — corrected from the first
              draft, which said "add it with `Add more information` and the plan will follow that
              instead". Every clause of that was unavailable from here (greptile P1), and the checks
              are in the code rather than in judgement:

                • `onAddInfo={() => onFocusComposer?.()}` (`TroubleshootingPanels.tsx:2847`) focuses
                  the composer. It does not open the method intake.
                • `supplyMethodDocument` opens with `if (!run) return { ok: false, message: 'There
                  is no parked run to answer.' }` and RESUMES a parked run. It is the intake path,
                  and it needs a park.
                • the backend refuses anyway: `resumeRun` throws unless
                  `run.status === 'waiting_for_user'` (`v2-run.service.ts:3006`), and this panel
                  renders a FINISHED run by definition.
                • and the control is not always even present: it sits behind
                  `canRun={live && !runInFlight}` (`:2842`, `:3256`) while this panel renders
                  regardless, so a report view showed the instruction with no button to follow it.

              So the offer is scoped to the moment the route exists — the intake park, which is
              where `supplyMethodDocument` and `methodSource` are reachable — and says nothing a
              reader cannot act on. A promise the app cannot keep is worse than no offer: it is the
              same failure as the judge, one layer up, telling the reader something confident and
              wrong. */}
          {method && (
            <p className="mt-2 rounded-md bg-background/70 px-2 py-1.5 text-[11px] text-muted-foreground ring-1 ring-border">
              Method from <span className="font-medium text-foreground">{method.title}</span>, found in
              your documents — the best match in the catalogue, not a procedure your team signed
              off. If you have a written one, send it when a run asks for a method and the plan will
              follow that instead.
            </p>
          )}
          {/* THE METHOD THE READER SUPPLIED, which until now reached nobody: the backend wrote
              `generatedFrom` onto the run INPUT and this panel is handed `finalOutput`, so an
              answer planned from an uploaded SOP said nothing about where its method came from —
              the one thing suite 2's acceptance bar asks it to say. Distinct from the two blocks
              above: not a registry procedure, and not one found on file. */}
          {generated && (
            <p className="mt-2 rounded-md bg-background/70 px-2 py-1.5 text-[11px] text-muted-foreground ring-1 ring-border">
              Method from <span className="font-medium text-foreground">{generated.title}</span>
              {generated.attributedTo === '' ? null : (
                <>
                  , supplied by{' '}
                  <span className="font-medium text-foreground">{generated.attributedTo}</span>
                </>
              )}
              . Planned from that document; nothing was saved to the library.
            </p>
          )}
          {/* THE UNITS FLAG. Its own block, and FIRST OF THE EVIDENCE BLOCKS — ahead of `omitted`
              and `sources`, not of the answer. The answer prose, `provisional` and `method` all
              render above it, and the number a reader would act on is in that prose either way, so
              "before they act on it" is a claim about the order of the evidence and nothing more.

              Titled, and the note arrives without its `BASIS CHECK:` lead (see `basisNotesOmitted`):
              that lead is the marker we match on, not something to typeset at a reader. The title is
              the export's, so the two surfaces name the same thing. */}
          {basis.length > 0 && (
            <div className="mt-2 rounded-md bg-amber-500/10 px-2 py-1.5 text-[11px] text-foreground ring-1 ring-amber-500/30">
              <p className="font-medium">Measurement basis</p>
              <div className="mt-1 space-y-0.5">
                {basis.map((note) => (
                  <p key={note}>{note}</p>
                ))}
              </div>
            </div>
          )}
          {/* THE FIGURES THE RUN MEASURED AND THE ANSWER DROPPED. Deliberately its own block rather
              than merged into the prose: the answer text is what the review agent grades and what
              the acceptance fixtures compare. Named by the step that gathered each, with no verdict
              attached — the numbers, and who measured them. */}
          {omitted.length > 0 && (
            <div className="mt-2 rounded-md bg-background/70 px-2 py-1.5 text-[11px] ring-1 ring-border">
              <p className="font-medium text-foreground">Measured, not stated above</p>
              <ul className="mt-1 space-y-0.5 text-muted-foreground">
                {omitted.map((entry) => (
                  <li key={entry.title}>
                    <span className="text-foreground">{entry.title}</span>: {entry.lines.join('; ')}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* THE EVIDENCE THE RUN GATHERED AND THE ANSWER DID NOT USE, for the legs that measured
              nothing. Beneath "Measured, not stated above" and for the same reason it sits there:
              a leg that computed a figure states the figure, and a leg that read a document has
              only its prose to show. Clipped server-side, and the last entry names any legs the
              per-answer cap left out — so "here is the evidence your answer skipped" cannot
              quietly mean "here is some of it". */}
          {documents.length > 0 && (
            <div className="mt-2 rounded-md bg-background/70 px-2 py-1.5 text-[11px] ring-1 ring-border">
              <p className="font-medium text-foreground">Gathered, not used above</p>
              <ul className="mt-1 space-y-1 text-muted-foreground">
                {documents.map((entry) => (
                  <li key={entry.title}>
                    <span className="text-foreground">{entry.title}</span>
                    {entry.excerpts.map((excerpt, at) => (
                      <p key={`${entry.title}-${at}`} className="mt-0.5 whitespace-pre-wrap">
                        {excerpt}
                      </p>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* WHAT THE ANSWER READ. Suite 1's ground truth ends with "every claim carries its
              source", and the gold answer's last line is a Sources list. This is the weaker, true
              half of it — the documents the evidence came from, not a per-claim mapping, which
              would look precise and be invented. */}
          {/* THE BASELINE AGAINST THE AFTER. Suite 1 asks what CHANGED, and the answer routinely
              states both figures without ever setting one against the other. This is that
              comparison, computed from the legs the run gathered — same tag, same statistic, two
              DIFFERENT windows, ordered by the dates the renders themselves report — so it is
              arithmetic on the evidence rather than a second opinion about it.

              BELOW "Measured, not stated above", and that order is the intended one rather than an
              accident of where the field was declared: the raw figures a leg computed come first,
              then the comparison drawn from them. A reader who distrusts the percentage can see
              the two numbers it came from directly above it. */}
          {moves.length > 0 && (
            <div className="mt-2 rounded-md bg-background/70 px-2 py-1.5 text-[11px] ring-1 ring-border">
              <p className="font-medium text-foreground">Between the periods gathered</p>
              <ul className="mt-1 space-y-0.5 text-muted-foreground">
                {moves.map((move, index) => (
                  /* The index rides in the key even though the producer cannot collide — `periodMoves`
                     pushes at most one move per (tag, statistic) pair, both drawn from Maps. But this
                     panel reads `finalOutput` as untrusted stored JSON everywhere else, and a duplicate
                     pair in a hand-edited or replayed result would silently mis-reconcile rather than
                     fail. Defensive keying is the same stance as the guards in `periodMoves` itself. */
                  <li key={`${move.subject}-${move.statistic}-${index}`}>
                    <span className="text-foreground">
                      {move.subject} {move.statistic}
                    </span>
                    : {periodMoveText(move)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {sources.length > 0 && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Sources:{' '}
              <span className="text-foreground">{sources.join(', ')}</span>
            </p>
          )}
          {/* THIS PROCEDURE HAS RUN BEFORE, and what it concluded then. Provenance, not
              evidence: it is deliberately kept out of the answer text (see the `priorRuns`
              write in `v2-run.service.ts`), because a previous conclusion is not evidence
              gathered for THIS question and the review agent would correctly flag its
              identifiers as ungrounded. So the reader gets it as a note beside the answer,
              where a stale conclusion is visible and ignorable rather than quoted as fact.

              Deliberately ONE line: the most recent run, plus a count for the rest. A list of
              five previews is a second answer on the page. */}
          {prior.length > 0 && (
            <p className="mt-2 rounded-md bg-background/70 px-2 py-1.5 text-[11px] text-muted-foreground ring-1 ring-border">
              Asked before{prior[0]?.at ? <> on {formatRunDate(prior[0].at)}</> : null}
              {prior.length > 1
                ? ` (${prior.length - 1} earlier run${prior.length === 2 ? '' : 's'})`
                : null}
              {prior[0]?.answerPreview ? (
                <>
                  {' — '}
                  <span className="text-foreground">“{prior[0].answerPreview}”</span>
                </>
              ) : null}
              . The plant may have changed since.
            </p>
          )}
          {/* What the reviewer actually objected to. The chip alone would leave the reader
              guessing, and the two lists answer different questions: a subject the answer
              never addresses (asked about PIC-1108, answered about PIC-1004) versus an
              identifier the answer asserts that no gathered row carries. */}
          {review && !review.holds && (
            <div className="mt-2 space-y-1.5 rounded-md bg-background/70 px-2 py-1.5 ring-1 ring-amber-500/20">
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Review
              </p>
              <ul className="list-disc space-y-0.5 pl-4 text-[11px] text-foreground">
                {review.issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
              {review.ungroundedIdentifiers.length > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  Not found in the gathered evidence:{' '}
                  <span className="font-medium text-foreground">
                    {review.ungroundedIdentifiers.join(', ')}
                  </span>
                </p>
              )}
              {/* The FIGURES a reader most needs named: an answer's verdict often rests on one
                  number, and a number the evidence does not carry was not measured. */}
              {review.ungroundedFigures.length > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  Figures no gathered row carries:{' '}
                  <span className="font-medium text-foreground">
                    {review.ungroundedFigures.join(', ')}
                  </span>
                </p>
              )}
              {review.unusedLegs.length > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  Gathered but unused:{' '}
                  <span className="font-medium text-foreground">
                    {review.unusedLegs.join(', ')}
                  </span>
                </p>
              )}
              {/* A GAP, NOT AN OMISSION. A step whose every row was a refusal gathered nothing, so
                  the answer had nothing of its to use — the opposite reading from the line above,
                  and the one a reader acts on by fixing retrieval rather than the prose.

                  "Gathered nothing" is the reader's words for what the logs call
                  `leg_refused:<n>` (`review_failed=`), off `review.refusedLegs` — named here
                  because the label and the token do not share a word, and someone grepping one
                  for the other finds no match (claude review on #30830). */}
              {review.refusedLegs.length > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  Gathered nothing:{' '}
                  <span className="font-medium text-foreground">
                    {review.refusedLegs.join(', ')}
                  </span>
                </p>
              )}
            </div>
          )}
          {refusal && (
            <div className="mt-2 space-y-2">
              {refusal.needed.length > 0 && (
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    To support this we would need
                  </p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm text-foreground">
                    {refusal.needed.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {refusal.supported.length > 0 && (
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Supported today
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {refusal.supported.map((item) => (
                      <span
                        key={item.blueprintId}
                        className="inline-flex items-center gap-1 rounded-md bg-background/70 px-2 py-0.5 text-[11px] font-medium text-foreground ring-1 ring-border"
                      >
                        {item.name}
                        {item.discipline && (
                          <span className="text-muted-foreground">· {item.discipline}</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* The report had to be DISCOVERED: it took three people two minutes to find
              the Build/Report tabs on the demo call (FS item 6), so a finished run now
              points at its own report from where the reader already is. */}
          {onViewReport && (
            <IntuiButton
              size="sm"
              variant="outline"
              className="mt-3 w-full"
              onClick={onViewReport}
            >
              View Report
            </IntuiButton>
          )}
          {hasMeta && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {subjects.map((subject, index) => (
                <span
                  key={str(subject.id) || index}
                  className="inline-flex items-center gap-1 rounded-md bg-background/70 px-2 py-0.5 text-[11px] font-medium text-foreground ring-1 ring-border"
                >
                  <span className="text-muted-foreground">{humanizeEnumValue(str(subject.kind))}:</span>
                  <span>{str(subject.label) || str(subject.id)}</span>
                </span>
              ))}
              {operators.length > 0 && (
                <span className="inline-flex items-center gap-1 rounded-md bg-background/70 px-2 py-0.5 text-[11px] font-medium text-foreground ring-1 ring-border">
                  <span className="text-muted-foreground">Method:</span>
                  <span>{operators.map(operatorLabel).join(' · ')}</span>
                </span>
              )}
            </div>
          )}
        </div>

        <ProblemSection value={output.problemStatement} concise={concise} />
        <PrimaryCandidateSection value={output.primaryCandidate} />
        {!(concise && actionsAreFollowUpOnly(output.actions)) && <ActionsSection value={output.actions} />}
        {!concise && <CauseMapSection value={output.causeMap} />}
        {!concise && <HypothesesSection value={output.hypotheses} />}
        {!concise && <ConnectedSystemsSection value={output.connectedSystemsChecked} />}
        {!(concise && safetyIsPlaceholder(output.safety)) && <SafetySection value={output.safety} />}
        {!concise && <LearningUpdatesSection value={output.learningUpdates} />}
        <AnalysisNarrativesSection value={output.analysisNarratives} />
        <GeneralResultSection output={output} />

        <OpenQuestionsSection
          blockedClaims={blockedClaims}
          assumptions={assumptions}
        />

        <EvidenceSection value={output.evidencePack} evidenceStepById={evidenceStepById} onSelectStep={onSelectStep} />

        {/* A friendly close that points to the obvious next moves. */}
        <p className="text-sm text-muted-foreground">
          What’s next? You can run this workflow again, refine the inputs, or start a new one.
        </p>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------- panel title */

/**
 * Panel header — the authored workflow title rather than a blanket "Troubleshooting result". A
 * `hazop_node_definition` run renders its own title ("HAZOP …"), not a troubleshooting frame it
 * never was (run d1275458). When a cached run carries no plan title, fall back to a label derived
 * from the run's GOAL (else its artifact), so a troubleshooting run still reads correctly.
 *
 * `templateId` is deliberately NOT a fallback: a raw template id (`hazop_node_definition`) is an
 * identifier, not user-facing copy, so it would read worse than the derived label. The docstring
 * previously claimed it mirrored the runner's `planPreview?.title ?? templateId` chain while the
 * body did something else, and the parameter was never read (review note, #26006).
 */
export function resultPanelTitle(
  run: Pick<WorkflowRunView, 'planPreview'>,
  output: Record<string, unknown>,
): string {
  const authored = str(run.planPreview?.title).trim()
  if (authored) return authored
  if (isTroubleshootingShaped(output, run.planPreview?.goal, run.planPreview?.family))
    return 'Troubleshooting result'
  return 'Workflow result'
}

/**
 * Artifact keys that only a TROUBLESHOOTING goal declares as required. A generated template declares
 * its own (`sections`, `node`, …), which is how a non-troubleshooting run is told apart here.
 *
 * Two things bound the risk here. It is matched with `.some()`, not equality, so a template that
 * drops or renames ONE key still classifies correctly — all seven shipped templates currently declare
 * all three (`troubleshooting-base`, `hazop-study`, `fmea-analysis`, `alarm-rationalization`,
 * `heat-material-balance`, plus `troubleshoot-5whys` / `troubleshooting-verified`, which spread
 * `troubleshootingBaseGoal`), pinned as a fixture in the tests so a change to that set has somewhere
 * to show up. And the failure mode is soft: a troubleshooting template declaring NONE of these falls
 * through to artifact-content detection rather than breaking — a mis-FRAME at worst, never a wrong
 * claim about what was found.
 */
const TROUBLESHOOTING_ARTIFACT_KEYS = ['causeMap', 'hypotheses', 'outcome'] as const

/**
 * Whether this run is troubleshooting-framed, deciding on the run's GOAL first and its artifact
 * only as a fallback.
 *
 * The goal has to lead, because the shared assembler emits `outcome` AND `problemStatement` for
 * EVERY workflow, troubleshooting or not — so their mere PRESENCE proves nothing, and keying on it
 * made this return `true` for a generated non-troubleshooting run, leaving the neutral verdict
 * unreachable and the panel headlining "No root cause confirmed yet." over a HAZOP node definition
 * (greptile #26006). `goal.enforced.requiredArtifactKeys` is the honest signal: every shipped
 * troubleshooting template declares `causeMap` / `hypotheses` / `outcome` there, and a generated
 * template declares its own keys instead. It is also decisive for the case the artifact cannot
 * settle — a run of EITHER kind that produced nothing.
 *
 * Without a goal (an older cached run), fall back to artifact CONTENT — non-empty troubleshooting
 * structure, or `generatedFindings` for the generic side — and only then to scaffold presence.
 */
export function isTroubleshootingShaped(
  output: Record<string, unknown> | undefined,
  goal?: unknown,
  family?: string,
): boolean {
  // The declared FAMILY is authoritative when present: every workflow is assembled through the
  // troubleshooting result contract (so the goal/artifact shape can't distinguish them — a HAZOP
  // node definition's goal is still forced to `requiredArtifactKeys:['outcome']`), but a `general`
  // family means it is NOT a fault-diagnosis run and must not be framed "No root cause confirmed"
  // (run 0b0ba27a). Only affects the empty-fallback wording + the title — a run that actually
  // produced a confirmed root cause still headlines it, since that branch precedes this.
  if (family === 'general') return false
  if (family === 'troubleshooting') return true
  const declared = asArray(asRecord(asRecord(goal)?.enforced)?.requiredArtifactKeys)
    .map((key) => str(key))
    .filter(Boolean)
  if (declared.length > 0) {
    return TROUBLESHOOTING_ARTIFACT_KEYS.some((key) => declared.includes(key))
  }
  if (!output) return false
  const outcome = asRecord(output.outcome)
  const hasTroubleshootingContent =
    asArray(outcome?.confirmedRootCauses).length > 0 ||
    asArray(outcome?.probableHypotheses).length > 0 ||
    asArray(output.causeMap).length > 0 ||
    asArray(output.hypotheses).length > 0
  if (hasTroubleshootingContent) return true
  const generated = asRecord(output.generatedFindings)
  if (generated && Object.keys(generated).length > 0) return false
  return outcome !== null || asRecord(output.problemStatement) !== null
}

/* ------------------------------------------------------------------ verdict */

export function verdictLine(
  outcome: Record<string, unknown> | null,
  output?: Record<string, unknown>,
  goal?: unknown,
  family?: string,
): string | null {
  if (!outcome) return null
  const roots = asArray(outcome.confirmedRootCauses)
  if (roots.length) {
    const first = asRecord(roots[0])
    return `Root cause identified: ${str(first?.statement) || `${roots.length} confirmed`}`
  }
  const probable = asArray(outcome.probableHypotheses)
  if (probable.length) {
    // Show the finding itself when there is ONE (mirrors the confirmed-cause
    // branch) — a single-hypothesis run should headline "reverse flow is not
    // pressure-driven", not a bare "1 probable cause" (run f370c963).
    const first = asRecord(probable[0])
    const stmt = str(first?.statement)
    if (probable.length === 1 && stmt) return `Probable (not yet confirmed): ${stmt}`
    // Keep the plural CONDITIONAL. The single case is only intercepted above when a
    // `statement` exists, and a lone model-produced hypothesis without one is reachable
    // (the derive always sets `statement`, a raw `outcome.probableHypotheses` entry need
    // not) — hardcoding `causes` rendered "1 probable causes — not yet confirmed."
    return `${probable.length} probable cause${probable.length > 1 ? 's' : ''} — not yet confirmed.`
  }
  // Link-4: a run whose structured outcome is empty can STILL carry a grounded
  // analysis the model wrote as prose (surfaced as `analysisNarratives` when the
  // synthesis step parse-failed). Don't headline it as a blank "No root cause
  // confirmed" — point the reader at the findings. Honest: it says the analysis
  // is present, NOT that a cause is confirmed (that would fabricate a KT
  // convergence the model never asserted).
  const narratives = asArray(output?.analysisNarratives)
  if (narratives.length) {
    return 'Grounded analysis available — see findings below (no root cause formally confirmed yet).'
  }
  // A general (non-troubleshooting) workflow that produced its DECLARED output — a HAZOP node
  // definition's sections/node/boundaries, carried as `generalResult` — headlines that the result
  // is ready, pointing at the curated render below. Unlike the reverted #26005, this reads the
  // declared output only (never the scaffolding-mixed `generatedFindings`), so it does not claim a
  // root cause and does not resurface internal synthesis fields.
  if (generalResultKeys(output).length) {
    return 'Analysis complete — see result below.'
  }
  // Final fallback: a troubleshooting run reads "no root cause"; a
  // non-troubleshooting workflow that produced nothing structured must not
  // claim a root-cause frame it never had.
  if (output && !isTroubleshootingShaped(output, goal, family)) {
    return 'No findings produced — see open questions below.'
  }
  return 'No root cause confirmed yet — see open questions below.'
}

/* ------------------------------------------ general (non-troubleshooting) result */

/** Field names tried in order to HEAD a record entry (a list item / nested record). */
// The general-result curation (lead keys, content gate, one-line flattener, key/value humanizers,
// fixed-section dedup, key selection) lives in the pure `generalResultGuard` module so the panel and
// the markdown export share ONE implementation and cannot drift. `generalResultKeys` is re-exported
// because the pure coverage tests import them from this module (`generalInlineValue` is the panel's
// historical name for the shared one-line flattener).
export {
  generalResultKeys,
  generalInline as generalInlineValue,
} from './generalResultGuard'
// Re-exported for the pure suites that assert ownership from this module's path (#26063). `export …
// from` rather than `export { … }`: these are not local bindings here, so the bare form would
// re-export `undefined`.
export {
  PANEL_FIXED_SECTION_KEYS as FIXED_SECTION_KEYS,
  SECTION_ITEM_HAS_CONTENT,
  SECTION_VALUE_HAS_CONTENT,
} from './generalResultGuard'

/** A record entry (list item / nested record): a bolded lead line + its remaining readable fields. */
/**
 * Whether `GeneralEntry` would render anything for `item` — mirrors its own conditions (a scalar, or a
 * record with a lead key or at least one renderable non-scaffolding field). Same discipline as the
 * fixed sections: the gate is the renderer's predicate, not a generic one.
 */
function generalEntryHasRenderableContent(item: unknown): boolean {
  if (typeof item === 'string') return item.trim().length > 0
  if (typeof item === 'number' || typeof item === 'boolean') return true
  const rec = asRecord(item)
  if (!rec) return false
  if (GENERAL_LEAD_KEYS.some((k) => str(rec[k]))) return true
  // MIRROR THE RENDERER, not the content gate. `hasGeneralContent` recurses to any depth while
  // `GeneralEntry` renders a rest key only when `generalInline` yields text, and they disagree at
  // exactly the depth this fix is for: hasGeneralContent({b:{c:1}}) is true, generalInline({b:{c:1}},'a')
  // is "". Gating on the former let the entry survive with every field rendering nothing, so the panel
  // drew <li><ul></ul></li> inside a titled Section — the titled empty box (graphite #26064).
  return Object.keys(rec).some((k) => !isScaffoldingKey(k) && generalInline(rec[k], k) !== '')
}

function GeneralEntry({ item }: { item: unknown }) {
  if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
    return <>{formatScalar(item)}</>
  }
  const rec = asRecord(item)
  if (!rec) return null
  const leadKey = GENERAL_LEAD_KEYS.find((k) => str(rec[k]))
  const lead = leadKey ? str(rec[leadKey]) : ''
  // Same predicate as the entry gate above: a key whose flattened value is empty must not produce a
  // list item, or the <ul> is built for rows that render nothing.
  const restKeys = Object.keys(rec).filter(
    (k) => k !== leadKey && !isScaffoldingKey(k) && generalInline(rec[k], k) !== '',
  )
  return (
    <>
      {lead ? <span className="font-medium">{lead}</span> : null}
      {restKeys.length > 0 && (
        <ul className={cn('flex flex-col gap-0.5', lead && 'mt-0.5 pl-3')}>
          {restKeys.map((k) => {
            const line = generalInline(rec[k], k)
            if (!line) return null
            return (
              <li key={k} className="text-sm text-muted-foreground">
                <span className="font-medium">{humanizeGeneralKey(k)}: </span>
                {line}
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}

/** Curated value for one top-level generalResult key: scalar → text, array → entry list, record →
 *  labeled field lines. Never dumps raw JSON — unrenderable depth is omitted. */
function renderGeneralValue(value: unknown, key?: string): React.ReactNode {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    // `key` so a top-level id-like field prints verbatim rather than humanized (see isIdLikeKey).
    return <p className="whitespace-pre-wrap text-sm text-foreground">{formatGeneralScalar(value, key)}</p>
  }
  const arr = asArray(value)
  if (arr.length) {
    // Filter entries BEFORE building the <ul>. Returning it whenever `arr.length > 0` drew a titled
    // empty box for `{ sections: [{ a: { b: { c: 1 } } }] }` — the content gate recurses to any depth,
    // `GeneralEntry` renders nothing that deep, and a <ul> of empty <li>s is still non-null, so the
    // caller's body-first check could not see it. The export suppresses that input already; this makes
    // the panel symmetric (graphite #26064).
    const entries = arr.filter((item) => generalEntryHasRenderableContent(item))
    if (!entries.length) return null
    return (
      <ul className="flex flex-col gap-1.5">
        {entries.map((item, i) => (
          <li key={i} className="text-sm text-foreground">
            <GeneralEntry item={item} />
          </li>
        ))}
      </ul>
    )
  }
  const rec = asRecord(value)
  if (rec) {
    return (
      <div className="text-sm text-foreground">
        <GeneralEntry item={rec} />
      </div>
    )
  }
  return null
}

/**
 * A general (non-troubleshooting) workflow's DECLARED result (`generalResult`), one Section per
 * top-level key — so a HAZOP node definition's sections / node / boundaries are visible, curated
 * (internal markers excluded, no raw JSON). The declared output only; the scaffolding-mixed
 * `generatedFindings` is never read here (that raw dump was reverted, #26005 → #26032).
 */
function GeneralResultSection({ output }: { output: Record<string, unknown> }) {
  const keys = generalResultKeys(output)
  if (!keys.length) return null
  const result = asRecord(output.generalResult) ?? {}
  // BODY FIRST, Section only when there is one — same reason as the export: `hasGeneralContent`
  // recurses to any depth while `renderGeneralValue` flattens a bounded number of levels, so a
  // deep-enough shape passes the gate and renders nothing. Building the body first means no future
  // depth can produce a titled empty box, instead of requiring the two predicates to stay in step
  // (self-review #26064).
  const rendered = keys
    .map((key) => ({ key, body: renderGeneralValue(result[key], key) }))
    .filter(({ body }) => body !== null && body !== undefined)
  if (!rendered.length) return null
  return (
    <>
      {rendered.map(({ key, body }) => (
        <Section key={key} title={humanizeGeneralKey(key)}>
          {body}
        </Section>
      ))}
    </>
  )
}

/* ----------------------------------------------------------------- sections */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <article>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {children}
    </article>
  )
}

/**
 * `concise` (a clean delivery) keeps the problem STATEMENT and drops the is/is-not
 * diagnostic rows — Expected / Actual / Extent / Ruled out. Those rows are how an
 * OPEN investigation is narrowed; on an answered question they read as leftovers
 * ("Expected: Not established from available data." under a green verdict) and
 * differ between workflow shapes for no reason.
 */
function ProblemSection({ value, concise = false }: { value: unknown; concise?: boolean }) {
  const p = asRecord(value)
  if (!p) return null
  const observed = str(p.observedDeviation)
  const facts: Array<[string, string]> = []
  if (!concise && str(p.expectedState)) facts.push(['Expected', str(p.expectedState)])
  if (!concise && str(p.actualState)) facts.push(['Actual', str(p.actualState)])
  if (!concise && str(p.extent)) facts.push(['Extent', str(p.extent)])
  const isNot = concise ? [] : asArray(p.isNot).map(str).filter(Boolean)
  if (!observed && !facts.length && !isNot.length) return null
  return (
    <Section title="Problem">
      {observed && <p className="text-sm text-foreground">{observed}</p>}
      {facts.length > 0 && (
        <dl className="mt-2 space-y-1">
          {facts.map(([k, v]) => (
            <div key={k} className="flex gap-2 text-sm">
              <dt className="w-20 shrink-0 text-muted-foreground">{k}</dt>
              <dd className="text-foreground">{v}</dd>
            </div>
          ))}
        </dl>
      )}
      {isNot.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">Ruled out: {isNot.join('; ')}</p>
      )}
    </Section>
  )
}

function PrimaryCandidateSection({ value }: { value: unknown }) {
  const c = asRecord(value)
  if (!c || !str(c.statement)) return null
  return (
    <Section title="Most likely cause">
      <div className="flex items-start gap-2">
        <p className="flex-1 text-sm text-foreground">{str(c.statement)}</p>
        {str(c.confidence) && <Badge tone="neutral">{humanizeEnumValue(str(c.confidence))} confidence</Badge>}
      </div>
    </Section>
  )
}

/** Safety statuses that carry NO warning — everything else keeps the Safety section visible. */
const BENIGN_SAFETY_STATUSES = new Set(['stable', 'unknown', ''])

/** The action types that mean "follow-up" (the `type` enum's non-operational values). */
const FOLLOW_UP_ACTION_TYPES = new Set(['monitoring', 'learning'])

/**
 * Is this `safety` block the PLACEHOLDER Khang asked to drop ("SAFETY: Unknown"), or does it
 * carry something a reader must see?
 *
 * Hidden on a clean delivery only when it has nothing to say: a `stable`/`unknown` status with
 * no safety instruction and no stop reason. A WARNING status, an instruction, or a stop reason
 * is never hidden. A stabilize-first run is already `guardrail_stopped` (never concise), but
 * `potentially_unsafe` / `unsafe_or_unstable` can ride a completed run, and suppressing that
 * for tidiness is not a trade worth making (greptile on #26393).
 */
function safetyIsPlaceholder(value: unknown): boolean {
  const safety = asRecord(value)
  if (!safety) return true
  // ALLOWLIST, not a denylist: only these statuses are "nothing to say". Anything else —
  // a warning level, a renamed level, a value this code has never seen — is SHOWN. A
  // denylist of the two known warning literals would silently suppress a new or renamed
  // hazard level on every green run, with no test able to catch it (khangngtien review).
  // Case-folded because the field's casing is not pinned end-to-end, and a mismatch must
  // not decide whether a hazard warning is visible.
  if (!BENIGN_SAFETY_STATUSES.has(str(safety.status).toLowerCase())) return false
  if (str(safety.stopReason)) return false
  return (
    asArray(safety.safetyInstructions)
      .map((item) => str(asRecord(item)?.instruction))
      .filter(Boolean).length === 0
  )
}

/**
 * Are these actions FOLLOW-UPS — monitoring / learning / "go fetch more data" — i.e. the
 * leftovers of an investigation that already concluded (Khang's screenshot: "Retrieve historian
 * data for PIC1126", Investigation, Unknown owner, Medium priority)?
 *
 * Anything the reader must ACT on stays visible even on a clean delivery: immediate or
 * corrective work, high priority, or an action gated on MOC/permit. Hiding operational guidance
 * because the run went green would lose the deliverable (greptile on #26393).
 */
function actionsAreFollowUpOnly(value: unknown): boolean {
  const actions = sectionItems('actions', value)
  if (!actions.length) return true
  // ALLOWLIST again: follow-up only when EVERY action is one of the two enum values that
  // actually mean "follow-up", and none is escalated. Expressing it as the complement of
  // "must-act" quietly swallowed `preventive` — operational work the reader is meant to do
  // ("add a low-flow interlock") — and would swallow any sixth enum value too
  // (khangngtien review). Unrecognized type ⇒ shown.
  return actions.every(
    (action) =>
      FOLLOW_UP_ACTION_TYPES.has(str(action.type)) &&
      str(action.priority) !== 'high' &&
      action.requiresMocOrPermit !== true,
  )
}

function ActionsSection({ value }: { value: unknown }) {
  // Content-filter, not just presence: a parse-failed / empty synthesis step can emit
  // `[{}]` or `[{owner:'ops'}]` — a non-empty array whose items carry no `action` text.
  // Length alone then rendered a titled "Recommended actions" box with blank rows; drop
  // items with no renderable action so an all-empty list hides the box entirely.
  const actions = sectionItems('actions', value)
  if (!actions.length) return null
  return (
    <Section title="Recommended actions">
      <ol className="flex flex-col gap-2">
        {actions.map((a, i) => (
          <li key={i} className="rounded-md border border-border bg-muted/30 p-2.5">
            <p className="text-sm text-foreground">{str(a.action)}</p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {str(a.owner) && <Badge tone="neutral">{humanizeEnumValue(str(a.owner))}</Badge>}
              {str(a.priority) && <Badge tone={a.priority === 'high' ? 'danger' : 'neutral'}>{humanizeEnumValue(str(a.priority))} priority</Badge>}
              {str(a.type) && <Badge tone="neutral">{humanizeEnumValue(str(a.type))}</Badge>}
              {str(a.verificationSignal) && <span className="text-xs text-muted-foreground">Verify: {str(a.verificationSignal)}</span>}
            </div>
          </li>
        ))}
      </ol>
    </Section>
  )
}

const CAUSE_GROUPS: Array<{ relation: string; label: string }> = [
  { relation: 'symptom', label: 'Symptoms' },
  { relation: 'immediate_cause', label: 'Immediate causes' },
  { relation: 'deeper_cause', label: 'Deeper causes' },
  { relation: 'barrier_gap', label: 'Barrier gaps' },
  { relation: 'consequence', label: 'Consequences' },
  { relation: 'unknown', label: 'Unknowns' },
]

function CauseMapSection({ value }: { value: unknown }) {
  // Content-filter: a node with no `statement` renders as a blank bullet inside its group.
  // The per-group render below already hides empty groups, but the outer box still showed
  // when nodes existed yet none had a statement — drop those so an all-empty map hides.
  const nodes = sectionItems('causeMap', value)
  if (!nodes.length) return null
  return (
    <Section title="Cause map">
      <div className="flex flex-col gap-3">
        {CAUSE_GROUPS.map(({ relation, label }) => {
          const group = nodes.filter((n) => n.relation === relation)
          if (!group.length) return null
          return (
            <div key={relation}>
              <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
              <ul className="flex flex-col gap-1.5">
                {group.map((n, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                    <StatusDot status={str(n.status)} />
                    <span className="flex-1">{str(n.statement)}</span>
                    {str(n.status) && <Badge tone={statusTone(str(n.status))}>{humanizeEnumValue(str(n.status))}</Badge>}
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </div>
    </Section>
  )
}

function HypothesesSection({ value }: { value: unknown }) {
  // Content-filtered like the other sections, but on EITHER field this section renders — a
  // statement-less hypothesis that carries `proposedControlledChecks` is real content, and filtering
  // on `statement` alone made it vanish rather than de-clutter.
  const hyps = sectionItems('hypotheses', value)
  if (!hyps.length) return null
  return (
    <Section title="Hypotheses">
      <div className="flex flex-col gap-2.5">
        {hyps.map((h, i) => {
          const checks = asArray(h.proposedControlledChecks).map(asRecord).filter(Boolean) as Record<string, unknown>[]
          return (
            <div key={i} className="rounded-md border border-border bg-muted/30 p-2.5">
              <div className="flex items-start gap-2">
                <p className="flex-1 text-sm text-foreground">{str(h.statement)}</p>
                {str(h.status) && <Badge tone={statusTone(str(h.status))}>{humanizeEnumValue(str(h.status))}</Badge>}
              </div>
              {checks.length > 0 && (
                <ul className="mt-1.5 flex flex-col gap-1 border-t border-border pt-1.5">
                  {checks.map((c, j) => (
                    <li key={j} className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{str(c.check) || str(c.expectedObservation)}</span>
                      {str(c.owner) && <> · {humanizeEnumValue(str(c.owner))}</>}
                      {str(c.expectedObservation) && str(c.check) && <> — {str(c.expectedObservation)}</>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        })}
      </div>
    </Section>
  )
}

function ConnectedSystemsSection({ value }: { value: unknown }) {
  // Content-filter: a row with neither `subject` nor `finding` renders blank, so drop it;
  // an all-empty list hides the "Connected systems" box.
  const systems = sectionItems('connectedSystemsChecked', value)
  if (!systems.length) return null
  return (
    <Section title="Connected systems">
      <ul className="flex flex-col gap-1.5">
        {systems.map((s, i) => (
          <li key={i} className="text-sm text-foreground">
            <span className="font-medium">{str(s.subject)}</span>
            {str(s.direction) && <span className="text-muted-foreground"> · {humanizeEnumValue(str(s.direction))}</span>}
            {str(s.finding) && <span className="text-muted-foreground"> — {str(s.finding)}</span>}
          </li>
        ))}
      </ul>
    </Section>
  )
}

function SafetySection({ value }: { value: unknown }) {
  const s = asRecord(value)
  if (!s) return null
  const status = str(s.status)
  const instructions = asArray(s.safetyInstructions).map((x) => str(asRecord(x)?.instruction)).filter(Boolean)
  if (!status && !instructions.length) return null
  const unsafe = status === 'unsafe_or_unstable' || status === 'potentially_unsafe'
  return (
    <Section title="Safety">
      <div className="flex items-center gap-2">
        {unsafe && <ShieldAlert className="size-4 text-amber-600" />}
        {status && <Badge tone={unsafe ? 'warning' : 'neutral'}>{humanizeEnumValue(status)}</Badge>}
        {s.stabilizeFirst === true && <span className="text-xs font-medium text-amber-700 dark:text-amber-500">Stabilize first</span>}
      </div>
      {instructions.length > 0 && (
        <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-sm text-foreground">
          {instructions.map((ins, i) => <li key={i}>{ins}</li>)}
        </ul>
      )}
    </Section>
  )
}

function LearningUpdatesSection({ value }: { value: unknown }) {
  // Content-filter, same defect as ActionsSection and the same shape from a parse-failed step:
  // `[{}]` drew a titled "Learning updates" box over a blank row, and `[{target:'sop'}]` drew one
  // containing the bare label "Sop: ".
  const updates = sectionItems('learningUpdates', value)
  if (!updates.length) return null
  return (
    <Section title="Learning updates">
      <ul className="flex flex-col gap-1">
        {updates.map((u, i) => (
          <li key={i} className="text-sm text-foreground">
            {str(u.target) && <span className="font-medium">{humanizeEnumValue(str(u.target))}: </span>}
            {str(u.recommendation)}
          </li>
        ))}
      </ul>
    </Section>
  )
}

/**
 * Grounded step prose that a phase wrote but that never parsed into the
 * structured schema (parse-fail). The backend surfaces it as `analysisNarratives`
 * so a correct analysis is not lost to "Not established" — render it verbatim,
 * clearly labeled as unstructured so it is not mistaken for a confirmed finding.
 */
/**
 * The step id the backend uses for a whole-run general-knowledge answer. It is not parse-fail prose and
 * must not wear the unstructured caveat: it is the deliberate, and often the ONLY, analysis the run has.
 */
const GENERAL_ANSWER_STEP = 'general-knowledge-answer'

/**
 * The general-knowledge answer, rendered AS the analysis.
 *
 * Run `a2e2405c` put it under "Analysis (unstructured)" beneath "Captured from step output that did not
 * parse into the structured result. Review before relying on it." — a caveat that is simply false here.
 * Nothing failed to parse; the engine asked for this answer and assembled it from a schema. Presenting it
 * as suspect scaffolding, under a step-id chip, buried the one thing on the page worth reading.
 *
 * One markdown document, not blank-line-split fragments: a list or a fenced block that contains a
 * blank line is still one structure, and `##` headings are markdown headings rather than a second
 * parser. NO box and NO caveat — this is the analysis, not scaffolding to be second-guessed.
 */
function GeneralAnswerSection({ text }: { text: string }) {
  if (!text) return null
  return (
    <Section title="Analysis">
      <AnswerProse text={text} className="text-sm" />
    </Section>
  )
}

function AnalysisNarrativesSection({ value }: { value: unknown }) {
  const records = asArray(value).map(asRecord).filter(Boolean) as Record<string, unknown>[]
  const all = records.map((n) => ({ step: str(n.step), text: str(n.text) })).filter((n) => n.text)
  // The general answer is split out and rendered plainly; only genuine parse-fail PROSE keeps the
  // caveat. JSON blobs (the `108301b9` shape) are filtered — they are not analysis a reader can review.
  const general = all.find((n) => n.step === GENERAL_ANSWER_STEP)
  const narratives = all.filter(
    (n) => n.step !== GENERAL_ANSWER_STEP && !isSerializedStructureBlob(n.text),
  )
  if (general && !narratives.length) return <GeneralAnswerSection text={general.text} />
  if (!narratives.length) return general ? <GeneralAnswerSection text={general.text} /> : null
  return (
    <>
      {general && <GeneralAnswerSection text={general.text} />}
      <Section title="Analysis (unstructured)">
        <p className="mb-2 text-xs text-muted-foreground">
          Captured from step output that did not parse into the structured result. Review before
          relying on it.
        </p>
        <div className="flex flex-col gap-3">
          {narratives.map((n, i) => (
            <div key={n.step || i} className="rounded-lg border border-border bg-muted/30 p-3">
              {n.step && (
                <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {humanizeEnumValue(n.step)}
                </p>
              )}
              <AnswerProse text={n.text} className="text-sm" />
            </div>
          ))}
        </div>
      </Section>
    </>
  )
}

function OpenQuestionsSection({
  blockedClaims,
  assumptions,
}: {
  blockedClaims: unknown[]
  assumptions: unknown[]
}) {
  // `uncertain`/`assumed` below were already content-filtered; `blocked` was not, so
  // `blockedClaims: [{}]` alone drew the whole amber panel with a "Could not be confirmed" heading
  // over a blank bullet.
  const blocked = sectionItems('blockedClaims', blockedClaims)
  // `remainingUncertainty` IS NO LONGER RENDERED. Everything it carried in practice was engine
  // bookkeeping restating what the panel already says: "no confirmed cause yet" is the verdict badge and
  // the blocked list below, and "need to distinguish process / equipment / controls / procedure /
  // data-quality" is our own methodology checklist. Run `aa62fc05` rendered SIX entries that were three
  // copies of the same two sentences, because the cause-analysis fallback appends them on every pass and
  // that template has three such steps.
  //
  // The honest content survives, in the two places that own it: a claim the run could not confirm is a
  // `blockedClaim` ("Could not be confirmed", below), and a judgment made on the reader's behalf is an
  // `assumption`. Neither is touched here.
  const assumed = assumptions.map(str).filter(Boolean)
  if (!blocked.length && !assumed.length) return null
  // WARN only for a genuine open question (a claim the run could not confirm). Remaining
  // uncertainty and assumptions are CAVEATS that accompany a delivered answer — every real
  // engineering deliverable carries them — so rendering them in the amber warning box put a
  // warning under a green "Clean run" verdict (greptile on #26312). Same content either way,
  // only the tone follows what the box actually contains.
  const blocking = blocked.length > 0
  return (
    <article
      className={cn(
        'rounded-lg border p-3',
        blocking
          ? 'border-amber-200 bg-amber-50/50 dark:border-amber-900/60 dark:bg-amber-950/20'
          : 'border-border bg-muted/30',
      )}
    >
      <h3
        className={cn(
          'mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide',
          blocking ? 'text-amber-700 dark:text-amber-500' : 'text-muted-foreground',
        )}
      >
        {blocking ? <AlertTriangle className="size-3.5" /> : <Info className="size-3.5" />}
        {blocking ? 'Unconfirmed claims' : 'Assumptions'}
      </h3>
      {blocked.length > 0 && (
        <div className="mt-2">
          <p className="mb-1 text-[11px] font-medium text-amber-700 dark:text-amber-500">Could not be confirmed</p>
          <ul className="flex flex-col gap-1.5">
            {blocked.map((b, i) => (
              <li key={i} className="text-sm text-foreground">
                <span>{str(b.claim)}</span>
                {str(b.blockedBy) && <span className="text-muted-foreground"> — blocked by {str(b.blockedBy)}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {assumed.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          {/* ONE LABEL PER CONCEPT. `!blocking` now implies `assumed.length > 0`, so the h3 above already
              reads "Assumptions" — repeating it four lines down is the same sin this PR removes (a heading
              restating the thing under it). The prefix is kept only when the h3 says something else. */}
          {blocking && <span className="font-medium">Assumptions: </span>}
          {assumed.join('; ')}
        </p>
      )}
    </article>
  )
}

/* ----------------------------------------------------------------- evidence */

const EVIDENCE_ICONS: Record<string, typeof FileText> = {
  document: FileText,
  datasheet: FileText,
  safety_study: FileText,
  historian: Activity,
  alarm_change_context: Activity,
  pnid: Database,
  control_loop: Database,
  data_quality: Database,
  user_context: Database,
}

function EvidenceSection({
  value,
  evidenceStepById,
  onSelectStep,
}: {
  value: unknown
  evidenceStepById: Map<string, WorkflowStepRunView>
  onSelectStep?: (step: WorkflowStepRunView) => void
}) {
  const [open, setOpen] = useState(false)
  const items = asArray(value).map(asRecord).filter(Boolean) as Record<string, unknown>[]
  if (!items.length) return null

  // Group counts by kind for the collapsed summary line.
  const counts = new Map<string, number>()
  for (const it of items) {
    const k = str(it.kind) || 'other'
    counts.set(k, (counts.get(k) ?? 0) + 1)
  }
  const summary = [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => `${n} ${humanizeEnumValue(k).toLowerCase()}`)
    .join(' · ')

  return (
    <article>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
      >
        {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        Sources ({items.length})
        {!open && <span className="ml-1 normal-case font-normal tracking-normal text-muted-foreground/80">— {summary}</span>}
      </button>

      {open && (
        <ul className="mt-2 flex flex-col gap-1.5">
          {items.map((item, index) => {
            const Icon = EVIDENCE_ICONS[str(item.kind)] ?? FileText
            const step = findProducingStep(item, evidenceStepById)
            // WHAT was consulted (file names / historian tags / sheet), never
            // the raw tool-call JSON the summary string carries.
            const source = describeEvidenceSource(item)
            return (
              <li key={evidenceItemKey(item, index)} className="flex items-start gap-2 rounded-md border border-border bg-muted/20 p-2 text-sm">
                <Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground">{source.label}</p>
                  {source.preview && <p className="mt-0.5 text-[12px] text-muted-foreground">{source.preview}</p>}
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                    {str(item.trust) && <span className="text-[11px] text-muted-foreground">{humanizeEnumValue(str(item.trust))}</span>}
                    {step && onSelectStep && (
                      <button type="button" className="text-[11px] font-medium text-cyan-700 hover:underline dark:text-cyan-500" onClick={() => onSelectStep(step)}>
                        Producing step
                      </button>
                    )}
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </article>
  )
}

/* ------------------------------------------------------------------- pieces */

function Badge({ tone = 'neutral', children }: { tone?: 'neutral' | 'warning' | 'danger' | 'success'; children: React.ReactNode }) {
  const tones = {
    neutral: 'bg-muted text-muted-foreground',
    success: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
    warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300',
    danger: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
  }
  return <span className={cn('rounded px-1.5 py-0.5 text-[11px] font-medium', tones[tone])}>{children}</span>
}

function StatusDot({ status }: { status: string }) {
  const color =
    status === 'supported' ? 'bg-green-500' : status === 'refuted' ? 'bg-red-500' : status === 'blocked' ? 'bg-amber-500' : 'bg-muted-foreground/40'
  return <span className={cn('mt-1.5 size-1.5 shrink-0 rounded-full', color)} aria-hidden />
}

function statusTone(status: string): 'neutral' | 'warning' | 'danger' | 'success' {
  if (status === 'supported') return 'success'
  if (status === 'refuted') return 'danger'
  if (status === 'blocked') return 'warning'
  return 'neutral'
}

/**
 * A DELIVERED run's human-facing verdict, or null when the status is not a
 * delivery (still running, or a genuine stop — failed / blocked / guardrail /
 * budget / cancelled, which keep their own status wording and red tone; a safety
 * stop must never read green because the artifact happens to carry text).
 */
function deliveredVerdict(
  status: string,
  output: Record<string, unknown>,
): { label: string; clean: boolean } | null {
  if (status !== 'completed' && status !== 'completed_with_unknowns') return null
  // Open questions FIRST — see reportStatusPill: rendered blocked claims are the
  // honest label even when findings accompany them.
  if (openQuestionCount(output) > 0) return { label: 'Open questions', clean: false }
  if (!resultHasAnswer(output)) return { label: 'No result', clean: false }
  return { label: 'Completed', clean: true }
}

/**
 * The outcome wording for a run, for chat-side surfaces (the result banner and the
 * run-history divider). Delivered runs read from what the artifact CONTAINS via
 * {@link deliveredVerdict}; anything else keeps its engine status wording. Exported so
 * the divider cannot drift from the card it sits above.
 */
export function runOutcomeLabel(
  run: Pick<WorkflowRunView, 'status' | 'finalOutput'>,
): string {
  const output = (run.finalOutput as Record<string, unknown> | null) ?? {}
  return deliveredVerdict(run.status, output)?.label ?? workflowStatusLabel(run.status)
}

function outcomeTone(status: string, delivered?: { clean: boolean } | null) {
  if (delivered ? delivered.clean : status === 'completed') {
    return {
      icon: CircleCheck,
      iconAccent: 'text-green-600',
      box: 'border-green-300 bg-green-50/60 dark:border-green-900 dark:bg-green-950/30',
      pill: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
    }
  }
  if (
    status === 'failed' ||
    status === 'blocked' ||
    status === 'guardrail_stopped' ||
    status === 'budget_limited' ||
    status === 'cancelled'
  ) {
    return {
      icon: CircleX,
      iconAccent: 'text-red-600',
      box: 'border-red-300 bg-red-50/60 dark:border-red-900 dark:bg-red-950/30',
      pill: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
    }
  }
  return {
    icon: AlertTriangle,
    iconAccent: 'text-amber-600',
    box: 'border-amber-300 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/30',
    pill: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300',
  }
}

/* ------------------------------------------------------------------ helpers */

/**
 * Coerce a value to a display string, TRIMMED.
 *
 * The trim is load-bearing, not tidiness: nearly every caller uses the result as a PRESENCE gate
 * (`const stmt = str(x); if (stmt) …`, `LEAD_KEYS.find((k) => str(r[k]))`), and an untrimmed
 * whitespace-only string is truthy — so a blank `statement` took the statement branch and rendered
 * the dangling label "Probable (not yet confirmed):    " instead of falling through to the count
 * (caught by this PR's own blank-statement pin). Trimming here fixes the whole class rather than one
 * call site, and matches the sibling `str()` in `reportUtils.ts`, which already trims — the two
 * helpers sharing a name with different semantics is what made this easy to miss.
 */
/**
 * Who supplied the method, when a run's procedure came from the asker rather than the
 * registry. Read field by field, for the same reason `parseSokRefusal` is.
 */
/**
 * The review agent's verdict, when the procedure declared a review step.
 *
 * Read defensively off a top-level `finalOutput` key, the same shape `provisionalSource` uses:
 * `finalOutput` is jsonb written by two engines, so a missing or malformed verdict renders
 * nothing rather than throwing.
 *
 * `holds: false` is REPORTED, never applied — the backend deliberately does not suppress an
 * answer its reviewer doubts, so this panel is the only place the doubt becomes visible. Until
 * it rendered, the review agent ran on every v2 answer and told nobody.
 */
function reviewVerdict(output: Record<string, unknown>): {
  holds: boolean
  issues: string[]
  unaddressedSubjects: string[]
  ungroundedIdentifiers: string[]
  ungroundedFigures: string[]
  unusedLegs: string[]
  refusedLegs: string[]
} | null {
  const raw = output.review
  if (typeof raw !== 'object' || raw === null) return null
  const record = raw as Record<string, unknown>
  if (typeof record.holds !== 'boolean') return null
  return {
    holds: record.holds,
    issues: strList(record.issues),
    unaddressedSubjects: strList(record.unaddressedSubjects),
    ungroundedIdentifiers: strList(record.ungroundedIdentifiers),
    // Absent on a verdict written before these checks existed — `strList` reads that as empty, so
    // an older run renders exactly what it used to.
    ungroundedFigures: strList(record.ungroundedFigures),
    unusedLegs: strList(record.unusedLegs),
    refusedLegs: strList(record.refusedLegs),
  }
}

function strList(value: unknown): string[] {
  return Array.isArray(value) ? value.map(str).filter((entry) => entry !== '') : []
}

/**
 * Earlier runs of the same procedure for this tenant, most recent first.
 *
 * Written into `finalOutput` at completion from what the run recalled at creation, so this
 * reads the same jsonb the rest of the panel does — no second request, and nothing to render
 * while the run is still going.
 *
 * Defensive for the same reason as its siblings: `finalOutput` is written by two engines
 * across many versions, and a malformed entry must render nothing rather than a blank row.
 */
function priorRuns(
  output: Record<string, unknown>
): Array<{ runId: string; at: string; answerPreview: string }> {
  return asArray(output.priorRuns)
    .map((entry) => {
      const record = asRecord(entry)
      return {
        runId: str(record?.runId),
        at: str(record?.at),
        answerPreview: str(record?.answerPreview),
      }
    })
    .filter((entry) => entry.runId !== '')
}

/**
 * `20 Aug 2026`, not an ISO string — the reader is judging "has the plant changed since",
 * which a timestamp answers worse than a date. An unparseable value renders as given rather
 * than as `Invalid Date`.
 */
function formatRunDate(at: string): string {
  const parsed = new Date(at)
  if (Number.isNaN(parsed.getTime())) return at
  // UTC: `at` is stored as ISO. Local TZ turns `2026-08-01T00:00:00.000Z` into
  // "31 Jul 2026" west of UTC, which is a different calendar day than the run.
  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/**
 * The document(s) the METHOD came from, when the run found one on file.
 *
 * Read defensively off a top-level `finalOutput` key, the same shape `provisionalSource` uses — and
 * deliberately NOT that key. `provisionalSource` says "one named person's unreviewed account"; this
 * says "your own procedure, found in your documents", which is a stronger claim and a different
 * one. An answer that cannot tell them apart invites the reader to assume the stronger.
 */
function methodFrom(output: Record<string, unknown>): { title: string } | null {
  const raw = output.methodFrom
  if (typeof raw !== 'object' || raw === null) return null
  const title = str((raw as Record<string, unknown>).title)
  return title ? { title } : null
}

/**
 * The DOCUMENT THE READER SENT, when the plan was generated from it.
 *
 * The third provenance shape, and a third claim: `provisionalSource` says "one named person's
 * unreviewed account", `methodFrom` says "your own procedure, found on file", and this says "the
 * document you supplied, planned from as written". Suite 2's acceptance bar is that the report
 * attributes its methodology to the uploaded document, and the generated path sets NEITHER of the
 * other two — it writes no registry row, so `provisionalSource` would name a blueprint that does
 * not exist.
 *
 * `attributedTo` MAY BE EMPTY, and an empty one drops the clause rather than the block — the title
 * is the fact suite 2 asks for, so a stored run written before the backend carried this key
 * renders the half it has rather than nothing. Not "optional": the field is a `string` and the
 * empty string IS the absence, so there is one sentinel and one check (`=== ''` in the JSX) rather
 * than an `undefined` to thread as well (self-review on #30869).
 */
function generatedFrom(
  output: Record<string, unknown>,
): { title: string; attributedTo: string } | null {
  const raw = output.generatedFrom
  if (typeof raw !== 'object' || raw === null) return null
  const record = raw as Record<string, unknown>
  const title = str(record.title)
  return title ? { title, attributedTo: str(record.attributedTo) } : null
}

/**
 * The documents this answer's evidence came from — the "collapsed citation list" this panel's own
 * docstring has promised since it was written, and which did not exist.
 *
 * Strings, already named for a reader by the backend (`usedSources`), so this renders them and does
 * not resolve anything: a workspace sheet key is deliberately not in the list.
 */
function usedSources(output: Record<string, unknown>): string[] {
  return asArray(output.usedSources)
    .map((value) => str(value))
    .filter((name) => name !== '')
}

/**
 * Figures the run MEASURED and the answer never stated — see `measuredFiguresOmitted`.
 *
 * The question is a comparison ("lower than it did in the summer"), and an answer holding one
 * period answers a different question. The review has been naming these legs and naming them
 * changed nothing, because it reports and never rewrites. This puts the number on screen.
 */
/**
 * The ROWS an unused document leg gathered that the answer never used.
 *
 * The sibling of `measuredOmitted` for the legs it cannot speak for — a document leg has no
 * statistic to state, so before this the review named the leg, `leg_coverage:N` counted it, and the
 * reader saw nothing. Same shape, `excerpts` rather than `lines`, and clipped server-side.
 */
function documentsOmitted(output: Record<string, unknown>): { title: string; excerpts: string[] }[] {
  return asArray(output.documentsOmitted)
    .map((entry) => {
      const record = typeof entry === 'object' && entry !== null ? (entry as Record<string, unknown>) : {}
      return {
        title: str(record.title),
        excerpts: asArray(record.excerpts).map((one) => str(one)).filter((one) => one !== ''),
      }
    })
    .filter((entry) => entry.title !== '' && entry.excerpts.length > 0)
}

function measuredOmitted(output: Record<string, unknown>): { title: string; lines: string[] }[] {
  return asArray(output.measuredOmitted)
    .map((entry) => {
      const record = typeof entry === 'object' && entry !== null ? (entry as Record<string, unknown>) : {}
      return {
        title: str(record.title),
        lines: asArray(record.lines).map((line) => str(line)).filter((line) => line !== ''),
      }
    })
    .filter((entry) => entry.title !== '' && entry.lines.length > 0)
}

/**
 * The measurement-basis warning the evidence raised and the answer did not repeat.
 *
 * Suite 1's ground truth makes surfacing this a requirement — readings near 150 against limits of
 * 2667-7794 USGPM — and declaring a breach across the gap an outright failure. db_ask writes the
 * sentence onto the row every time; it reached the reader in three of eight dev runs.
 */
function basisOmitted(output: Record<string, unknown>): string[] {
  return asArray(output.basisOmitted)
    .map((note) => str(note))
    .filter((note) => note !== '')
}

function provisionalSource(
  output: Record<string, unknown>,
): { attributedTo: string; kind: 'canvas' | 'supplied' | 'approved' | 'unstamped' } | null {
  const raw = output.provisionalSource
  if (typeof raw !== 'object' || raw === null) return null
  const record = raw as Record<string, unknown>
  const attributedTo = str(record.attributedTo)
  // ABSENT OR UNRECOGNISED ⇒ `unstamped`, which asserts NOTHING about what the person did — and
  // this direction is the fix. See the render site (~line 215) for the sentence it selects; that
  // block and this one describe the same QC-round-1 defect from the two ends of the file.
  //
  // The first version of this fix defaulted to `approved`, on the grounds that it claims less than
  // `supplied`. It does — but it is not TRUE of the case it most changes: a legacy run that really
  // was a supplied draft would foot "Plan approved by <person>", and that person approved no plan;
  // they supplied a method. Weaker than the old lie is still a lie, so the unknown case now makes
  // no provenance claim at all (review on #30896, greptile P1).
  //
  // It was `supplied`, reasoned as "the pre-existing sentence, so a legacy run's wording is
  // unchanged". But `supplied` is the strongest claim of the three: it names a person as having
  // SUPPLIED A METHOD and adds "Saved as a draft procedure pending sign-off", which asserts a
  // registry WRITE. Defaulting to it means making both claims on the least information we have.
  //
  // Measured on dev, 2026-09-03. Every suite-1 run that evening logged
  // `[WORKFLOW_V2_PREWARM] trigger=direct_answer` — never `supplied_draft`, so
  // `startFromSuppliedDraft` never ran, no `createDraft` was called, and nobody supplied
  // anything. The reports still footed "Method supplied by <the reader> and not yet reviewed.
  // Saved as a draft procedure pending sign-off" (`f45cb8e1`, `0624691c`), because the stored
  // `provisionalSource` reached this reader without a recognised `kind` and fell through to here.
  //
  // That is the SAME defect the block at the render site describes — "nothing was supplied and no
  // draft was written… QC round 1 filed it" — reappearing through the fallback rather than through
  // a producer. Both backend producers on this path stamp correctly (`canvas` at the canvas start,
  // `approved` on an approved-plan resume); it is the default that lied.
  //
  // AND UNSTAMPED RECORDS ARE HISTORY, not live traffic — checked, because it decides whether this
  // fallback is load-bearing or archival. Every construction site in `v2-run.service.ts` stamps a
  // kind: `canvas` at the canvas start (`:2336`), `supplied` on the two intake paths (`:4370`,
  // `:5363`), `approved` on the approved-plan resume (`:4899`). Every other write forwards an
  // existing `provisionalSource` through `provisionalSourceOf(run.input)`, which preserves whatever
  // was stamped. So a record with no `kind` was stored before the field existed, and this branch
  // renders history rather than anything a producer can still create.
  //
  // THE ONE COPY OF THE UNION THAT CANNOT BE SHARED — the backend owns `PROVISIONAL_KINDS` and
  // `isProvisionalKind` beside the interface, and this package cannot import from it. So the list
  // is duplicated by necessity rather than neglect, and the fallback is what makes that safe: a
  // kind this reader has not learned yet reads as the claim that cannot be wrong.
  const kind =
    record.kind === 'canvas'
      ? 'canvas'
      : record.kind === 'supplied'
        ? 'supplied'
        : record.kind === 'approved'
          ? 'approved'
          : 'unstamped'
  return attributedTo ? { attributedTo, kind } : null
}

function str(value: unknown): string {
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

/** First of the given values that is a non-empty array (schema vs lifted shape). */
function firstArray(...candidates: unknown[]): unknown[] {
  for (const c of candidates) if (Array.isArray(c) && c.length) return c
  return []
}

/**
 * The readings' own span as two dates, or EMPTY STRINGS when a stored result carries none.
 *
 * A result stored before the window existed has no `window` key, and `periodMoveText` renders the
 * figure without a period rather than `(summer, →)` — so an old artifact keeps rendering. The
 * `?? {}` here is not the cast the docblock below argues against: `str()` guards both fields, so a
 * non-object `raw` yields two empty strings and takes exactly the missing-window path.
 */
function windowOf(raw: unknown): { from: string; to: string } {
  const window = asRecord(raw) ?? {}
  return { from: str(window.from), to: str(window.to) }
}

/**
 * What MOVED between the periods the run gathered — the comparison the answer did not draw.
 *
 * Suite 1's second half is "what changed", and the gold answers it by setting a baseline against
 * an after. Computed by the backend from the legs (`periodMoves`); this only renders it.
 *
 * Read through `asRecord` like the rest of this file, not through `?? {}` casts: `from` may be a
 * string or a number in stored output, and a cast would let a non-object reach `str(from.value)`.
 * The value guard below catches that either way, so this is the file's convention rather than a
 * bug fix — but the convention is the reason a reader can trust the guard.
 */
function periodMoves(output: Record<string, unknown>): PeriodMove[] {
  return asArray(output.periodMoves).flatMap((raw) => {
    const move = asRecord(raw) ?? {}
    const from = asRecord(move.from) ?? {}
    const to = asRecord(move.to) ?? {}
    const subject = str(move.subject)
    const statistic = str(move.statistic)
    if (subject === '' || statistic === '' || str(from.value) === '' || str(to.value) === '')
      return []
    return [
      {
        subject,
        statistic,
        from: { title: str(from.title), value: str(from.value), window: windowOf(from.window) },
        to: { title: str(to.title), value: str(to.value), window: windowOf(to.window) },
        changePct: typeof move.changePct === 'number' ? move.changePct : undefined,
      },
    ]
  })
}

interface PeriodMove {
  subject: string
  statistic: string
  from: { title: string; value: string; window: { from: string; to: string } }
  to: { title: string; value: string; window: { from: string; to: string } }
  changePct?: number
}

/**
 * One move as a reader reads it: `161.3512 (summer) → 152.4700 (now) — -5.5%`.
 *
 * A period's TITLE is optional here while its value is not. `periodMoves` drops a move whose value
 * is missing — a comparison with nothing to compare is not a comparison — but a missing title only
 * costs the reader the label, so the parenthetical is omitted rather than the whole line: the
 * backend always sets one from the step, and rendering `161.3512 ()` for the day it does not would
 * make a display gap look like a data gap.
 *
 * The three change states are the three `percentChange` returns, so the panel cannot render a state
 * the producer cannot emit: absent (a zero baseline has no percentage, and both values still show),
 * zero (an unchanged pair is kept deliberately — "the setpoint did not move" is half of what
 * changed), and signed. `+` is explicit because a rise otherwise loses its direction: `-5.5`
 * carries its sign for free and `8.1` does not.
 */
function periodMoveText(move: PeriodMove): string {
  // THE WINDOW, NOT THE TITLE'S CLAIM ABOUT IT. Dev runs `8caa79fe` and `b6fa39ea` (2026-09-03)
  // both wrote "Summer average (2025-07-01 to 2025-08-31): 161.35" for a figure computed over
  // 2025-06-01 → 2025-09-01, and nothing on screen contradicted the invented window. These dates
  // are the readings' own span, off the coverage note db_ask wrote.
  const named = (value: string, title: string, window: { from: string; to: string }) => {
    const period = window.from === '' || window.to === '' ? '' : `, ${window.from}→${window.to}`
    return title === '' ? `${value}${period}` : `${value} (${title}${period})`
  }
  const change =
    move.changePct === undefined
      ? ''
      : move.changePct === 0
        ? ' — unchanged'
        : ` — ${move.changePct > 0 ? '+' : ''}${move.changePct}%`
  const from = named(move.from.value, move.from.title, move.from.window)
  const to = named(move.to.value, move.to.title, move.to.window)
  return `${from} → ${to}${change}`
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

/** Display label for an applied operator/methodology. */
const OPERATOR_LABELS: Record<string, string> = { five_whys: '5 Whys', rca: 'RCA' }
function operatorLabel(operator: string): string {
  return OPERATOR_LABELS[operator] ?? humanizeEnumValue(operator)
}

/**
 * Humanize enum-shaped string values for display (e.g. "trusted_plant_system" →
 * "Trusted plant system", "unresolved" → "Unresolved"). Only lowercase tokens
 * joined by underscores qualify; tags/IDs/prose (digits, uppercase, hyphens,
 * dots, spaces) are returned verbatim so "AIT1006", "ev-define-problem-1", and
 * sentences are preserved. Non-strings stringify as before.
 */
export function humanizeEnumValue(value: string): string {
  if (!/^[a-z]+(_[a-z]+)*$/.test(value)) return value
  const spaced = value.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * Identifier/reference field names whose values are never enum-humanized, so an
 * underscore-namespaced id (e.g. "ev_define_problem", "hyp_interlock_trigger")
 * is preserved verbatim rather than mangled into prose. Covers `id`, camelCase
 * `*Id`/`*Ref`, and snake `*_id`/`*_ref`.
 */
export function isIdLikeKey(key: string): boolean {
  return /(^id$|Id$|Ref$|_id$|_ref$)/.test(key)
}

export function formatScalar(value: unknown, key?: string): string {
  if (typeof value !== 'string') return String(value)
  if (key !== undefined && isIdLikeKey(key)) return value
  return humanizeEnumValue(value)
}

function buildEvidenceStepMap(steps: WorkflowStepRunView[]): Map<string, WorkflowStepRunView> {
  const evidenceStepById = new Map<string, WorkflowStepRunView>()
  for (const step of steps) {
    collectEvidenceIds(step.evidence, step, evidenceStepById)
    collectEvidenceIds(step.output, step, evidenceStepById)
  }
  return evidenceStepById
}

function collectEvidenceIds(
  value: unknown,
  step: WorkflowStepRunView,
  evidenceStepById: Map<string, WorkflowStepRunView>,
  depth = 0,
) {
  if (depth > 4) return
  if (Array.isArray(value)) {
    for (const item of value) collectEvidenceIds(item, step, evidenceStepById, depth + 1)
    return
  }
  const rec = asRecord(value)
  if (!rec) return
  if (typeof rec.id === 'string' && !evidenceStepById.has(rec.id) && (typeof rec.summary === 'string' || Array.isArray(rec.sourceRefs))) {
    evidenceStepById.set(rec.id, step)
  }
  for (const entry of Object.values(rec)) collectEvidenceIds(entry, step, evidenceStepById, depth + 1)
}

function findProducingStep(item: unknown, evidenceStepById: Map<string, WorkflowStepRunView>): WorkflowStepRunView | undefined {
  const rec = asRecord(item)
  if (!rec || typeof rec.id !== 'string') return undefined
  return evidenceStepById.get(rec.id)
}

function evidenceItemKey(item: unknown, index: number): string {
  const rec = asRecord(item)
  if (rec && typeof rec.id === 'string') return rec.id
  return String(index)
}
